import { Injectable } from '@angular/core';
import { SwitchAttrsClientService, toSwitchAttr } from '@mode/shared/data-access-webapp';
import { ToastService } from '@mode/shared/util-toast';
import { SwitchAttrsActions } from '@mode/switch-views/contract';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { makeRequestAttrFromCalc } from '../switch-resource-helpers';

@Injectable()
export class SwitchAttrsEffects {
  constructor(
    private actions$: Actions,
    private switchAttrsService: SwitchAttrsClientService,
    private toastService: ToastService
  ) {}

  createSwitchViewAttr$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SwitchAttrsActions.createSwitchViewAttrFromDataview),
      concatMap((action) =>
        this.switchAttrsService.createAttr(action.token, makeRequestAttrFromCalc(action.attr)).pipe(
          map((response) =>
            SwitchAttrsActions.createSwitchViewAttrFromDataViewSuccess({
              token: action.token,
              attr: toSwitchAttr(response),
            })
          ),
          catchError((error) => of(SwitchAttrsActions.createSwitchViewAttrFailure({ error })))
        )
      )
    )
  );

  createSwitchViewAttrSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchAttrsActions.createSwitchViewAttrSuccess),
        tap(() => this.toastService.success('Field saved'))
      );
    },
    { dispatch: false }
  );

  createSwitchViewAttrFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchAttrsActions.createSwitchViewAttrFailure),
        tap(() => this.toastService.error('There was a problem saving your field'))
      );
    },
    { dispatch: false }
  );

  updateSwitchViewAttr$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SwitchAttrsActions.updateSwitchViewAttrFromDataView),
      concatMap((action) =>
        this.switchAttrsService
          .updateAttr(action.token, action.attr.token as string, makeRequestAttrFromCalc(action.attr))
          .pipe(
            map((response) =>
              SwitchAttrsActions.updateSwitchViewAttrFromDataViewSuccess({
                attr: toSwitchAttr(response),
                token: action.token,
                queryToken: action.queryToken,
              })
            ),
            catchError((error) => of(SwitchAttrsActions.updateSwitchViewAttrFailure({ error })))
          )
      )
    );
  });

  updateSwitchViewAttrSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchAttrsActions.updateSwitchViewAttrSuccess),
        tap(() => this.toastService.success('Field updated'))
      );
    },
    { dispatch: false }
  );

  updateSwitchViewAttrFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchAttrsActions.updateSwitchViewAttrFailure),
        tap(() => this.toastService.error('There was a problem updating your field'))
      );
    },
    { dispatch: false }
  );

  deleteSwitchViewAttr$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SwitchAttrsActions.deleteSwitchViewAttrFromDataView),
      concatMap((action) =>
        this.switchAttrsService.deleteAttr(action.attr.token as string, action.token).pipe(
          map(() =>
            SwitchAttrsActions.deleteSwitchViewAttrSuccess({
              attr: action.attr,
              token: action.token,
              queryToken: action.queryToken,
            })
          ),
          catchError((error) => of(SwitchAttrsActions.deleteSwitchViewAttrFailure({ error })))
        )
      )
    );
  });

  deleteSwitchViewAttrSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchAttrsActions.deleteSwitchViewAttrSuccess),
        tap(() => this.toastService.success('Field removed'))
      );
    },
    { dispatch: false }
  );

  deleteSwitchViewAttrFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchAttrsActions.deleteSwitchViewAttrFailure),
        tap(() => this.toastService.error('There was a problem removing your field'))
      );
    },
    { dispatch: false }
  );
}
